import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '../api/api.service';
import { BatchSystemUser } from '../../models/batch-system-user';
import { PagedResponse } from '../../models/paged-response';
import { Profile } from '../../models/profile';
import { ProfileToAssign } from '../../models/profile-to-assign';
import { SystemUser } from '../../models/system-user';
import { Session } from '../../models/session';

@Injectable({
  providedIn: 'root'
})
export class SystemUsersService {

  private readonly _api: ApiService = inject(ApiService);

  /**
   * Get logged in SystemUser record.
   * @returns SystemUser record.
   */
  public getLoggedInSystemUser$(): Observable<Session> {
    return this._api.get$('systemusers', 'me') as Observable<Session>;
  }

  /**
   * Get paged SystemUsers list.
   * @param page Page to get.
   * @param email Email filter.
   * @returns Paged response for SystemUsers.
   */
  public getSystemUsers$(page: number = 1, email: string = '') {
    const params = new HttpParams().append('page', page).append('filters', `(email|fullName)@=${email}`);
    return this._api.get$('systemusers', '', undefined, params) as Observable<PagedResponse<SystemUser>>;
  }

  /**
   * Get specific SystemUser record.
   * @param id SystemUser unique identifier.
   * @returns SystemUser record.
   */
  public getSystemUser$(id: string) {
    return this._api.get$('systemusers', id) as Observable<SystemUser>;
  }

  /**
   * Create a new SystemUser record to access in site.
   * @param email Azure AD user email. 
   * @returns SystemUser record.
   */
  public createSystemUser$(email: string, profiles: Profile[] = []) {
    const profilesToASsign: ProfileToAssign[] = [];
    profiles.forEach(x => profilesToASsign.push({ id: x.id }));
    return this._api.post$('systemusers', '', undefined, undefined, { email, profiles: profilesToASsign }) as Observable<SystemUser>;
  }

  /**
   * Update SystemUser status.
   * @param systemUser SystemUser record to update.
   */
  public updateSystemUserStatus$(systemUser: SystemUser) {
    return this._api.put$('systemusers', `${systemUser.id}/${systemUser.status}`) as Observable<void>;
  }

  /**
   * Delete a SystemUser record.
   * @param id systemUser unique identifier.
   */
  public deleteSystemUser$(id: string) {
    return this._api.delete$('systemusers', id) as Observable<void>;
  }

  /**
   * Process batch SystemUsers to create.
   * @param batch Batch body request.
   * @returns Unkown users.
   */
  public batchCreateSystemUser$(batch: BatchSystemUser) {
    return this._api.post$('systemusers', 'add-batch', undefined, undefined, batch, 'text') as Observable<any>;
  }

  /**
   * Process batch SystemUsers to inactivate.
   * @param batch Batch body request.
   * @returns Unkown users.
   */
  public batchDeactivateSystemUser$(batch: BatchSystemUser) {
    return this._api.delete$('systemusers', 'deactivate-batch', undefined, undefined, batch, 'text') as Observable<any>;
  }
}